import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "资讯", to: "/news" },
  { label: "资源", to: "/resources" },
  { label: "话题", to: "/topics" },
  { label: "关于", to: "/about" },
  { label: "联系", to: "/contact" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/news/all?q=${encodeURIComponent(value)}` : "/news");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-background-200/90 bg-background-50/95 backdrop-blur">
      <div className="hidden border-b border-primary-800/40 bg-primary-900 text-background-100 md:block">
        <div className="mx-auto flex h-9 max-w-[1280px] items-center justify-between px-6 text-[11px] tracking-wide">
          <p className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-accent-500 text-[9px] font-bold text-foreground-950">
              18
            </span>
            成人内容资讯与资源平台 · 所有内容均为虚构演示
          </p>
          <p className="flex items-center gap-4 text-background-200">
            <span>仅面向 18 岁以上成年人</span>
            <Link to="/about" className="cursor-pointer text-accent-300 transition hover:text-accent-200">
              内容声明
            </Link>
          </p>
        </div>
      </div>

      <div className="mx-auto flex h-[66px] max-w-[1280px] items-center gap-6 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5" aria-label="18禁 首页">
          <span className="flex h-10 w-10 flex-col items-center justify-center rounded-md bg-primary-600 text-background-50">
            <span className="font-heading text-[15px] font-bold leading-none">18</span>
            <span className="mt-0.5 text-[9px] leading-none tracking-[0.12em]">禁</span>
          </span>
          <span className="hidden leading-tight sm:block">
            <span className="block font-heading text-base font-semibold tracking-[0.06em] text-foreground-950">18禁</span>
            <span className="block text-[9px] tracking-[0.18em] text-foreground-500">ADULT ARCHIVE</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`relative cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition ${
                isActive(item.to)
                  ? "text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
              {isActive(item.to) ? (
                <span className="absolute inset-x-3 -bottom-[1px] h-[2px] rounded-full bg-accent-500"></span>
              ) : null}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden w-[280px] lg:block" role="search">
          <label className="relative flex items-center">
            <i className="ri-search-line absolute left-3 text-sm text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索资讯、资源、话题"
              name="q"
              className="h-9 w-full rounded-md border border-background-300/80 bg-background-100/80 pl-9 pr-3 text-sm text-foreground-800 outline-none transition placeholder:text-foreground-400 focus:border-primary-300 focus:bg-background-50"
            />
          </label>
        </form>

        <button
          type="button"
          onClick={() => setMobileOpen((value) => !value)}
          aria-label="打开导航菜单"
          aria-expanded={mobileOpen}
          className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/80 text-foreground-800 md:hidden"
        >
          <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true"></i>
        </button>
      </div>

      {mobileOpen ? (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={submitSearch} role="search" className="mb-4">
            <label className="relative flex items-center">
              <i className="ri-search-line absolute left-3 text-sm text-foreground-400" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索资讯、资源、话题"
                name="q"
                className="h-10 w-full rounded-md border border-background-300/80 bg-background-100/80 pl-9 pr-3 text-sm outline-none focus:border-primary-300 focus:bg-background-50"
              />
            </label>
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex cursor-pointer items-center justify-between rounded-md border px-3 py-3 text-sm transition ${
                  isActive(item.to)
                    ? "border-primary-200 bg-primary-50 text-primary-700"
                    : "border-background-200 bg-background-100/60 text-foreground-700"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-base text-foreground-400" aria-hidden="true"></i>
              </Link>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}